import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Ui";
import { Briefcase, CheckSquare, Gavel, Receipt } from "lucide-react";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const PERIODS = [
    { value: "day", label: "Today" },
    { value: "week", label: "This week" },
    { value: "month", label: "This month" },
    { value: "year", label: "This year" },
];

export default function Productivity() {
    const [period, setPeriod] = useState("month");
    const [data, setData] = useState({ rows: [] });
    useEffect(() => { api.get("/analytics/productivity", { params: { period } }).then(r => setData(r.data)); }, [period]);
    const tableCtrl = useTableControls(data.rows || []);

    return (
        <>
            <PageHeader title="Staff productivity" subtitle="Cases owned, tasks completed, hearings recorded and invoices billed — per member, per period."/>

            <div className="mb-6 flex gap-2 flex-wrap">
                {PERIODS.map(p => (
                    <button key={p.value} onClick={() => setPeriod(p.value)} data-testid={`prod-period-${p.value}`}
                        className={`text-xs uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${period === p.value ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-muted-foreground"}`}>
                        {p.label}
                    </button>
                ))}
            </div>

            {data.rows.length === 0 ? (
                <EmptyState title="No staff data yet"/>
            ) : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[720px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh field="name" ctrl={tableCtrl}>Member</SortableTh>
                                <SortableTh field="role" ctrl={tableCtrl}>Role</SortableTh>
                                <SortableTh field="cases_owned" ctrl={tableCtrl}><Briefcase className="w-3.5 h-3.5 inline mr-1.5"/> Cases</SortableTh>
                                <SortableTh field="tasks_open" ctrl={tableCtrl}><CheckSquare className="w-3.5 h-3.5 inline mr-1.5"/> Open</SortableTh>
                                <SortableTh field="tasks_done" ctrl={tableCtrl}><CheckSquare className="w-3.5 h-3.5 inline mr-1.5"/> Done</SortableTh>
                                <SortableTh field="hearings_created" ctrl={tableCtrl}><Gavel className="w-3.5 h-3.5 inline mr-1.5"/> Hearings</SortableTh>
                                <SortableTh field="invoices_total" ctrl={tableCtrl}><Receipt className="w-3.5 h-3.5 inline mr-1.5"/> Billed</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {tableCtrl.rows.map(r => (
                                <tr key={r.user_id} className="border-b border-border hover:bg-muted/50" data-testid={`prod-row-${r.user_id}`}>
                                    <td className="px-4 py-3">{r.name}</td>
                                    <td className="px-4 py-3 overline">{r.role.replace(/_/g, " ")}</td>
                                    <td className="px-4 py-3 font-mono">{r.cases_owned}</td>
                                    <td className="px-4 py-3 font-mono text-muted-foreground">{r.tasks_open}</td>
                                    <td className="px-4 py-3 font-mono text-primary">{r.tasks_done}</td>
                                    <td className="px-4 py-3 font-mono">{r.hearings_created}</td>
                                    <td className="px-4 py-3 font-mono">₹{r.invoices_total.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
